"use client";

import { LocaleLink } from "@/components/shared/LocaleLink";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

function UserCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function AccountMenuButton({ className }: { className?: string }) {
  const { logout } = useAuth();
  const { t, isRtl } = useLanguage();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleLogout = () => {
    setOpen(false);
    logout();
    router.refresh();
  };

  const itemClass =
    "flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#130F0E] hover:bg-[#F4F4F4] transition-colors";

  return (
    <div className={`relative shrink-0 ${className ?? ""}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-[#DD0428] text-[#BD2226] hover:bg-[#BD2226] hover:text-white transition-colors"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t("nav.accountMenu")}
      >
        <UserCircleIcon className="w-5 h-5" />
      </button>
      {open ? (
        <div
          role="menu"
          className={`absolute top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-[#E5E5E5] py-1 z-[120] ${
            isRtl ? "left-0" : "right-0"
          }`}
        >
          <LocaleLink
            href="/my-account"
            role="menuitem"
            className={itemClass}
            onClick={() => setOpen(false)}
          >
            <svg
              className="w-4 h-4 text-[#6B7280]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            {t("nav.myAccount")}
          </LocaleLink>
          <button
            type="button"
            role="menuitem"
            className={`${itemClass} w-full text-start border-t border-[#E5E5E5] mt-1 pt-3 text-[#BD2226] hover:text-[#C00322]`}
            onClick={handleLogout}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t("nav.logout")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
